<template>
    <div class="fac-chart">
        <div class="fac-chart__header">
            <div>
                <p class="fac-chart__eyebrow">Workspace mix</p>
                <h3 class="fac-chart__title">Academic vs admin offices</h3>
            </div>
        </div>
        <div v-if="!hasData" class="fac-chart__empty">No office data available yet.</div>
        <apexchart
            v-else
            type="radialBar"
            height="300"
            :options="chartOptions"
            :series="series"
            @dataPointSelection="onSliceSelect"
            @legendClick="onLegendClick"
        />
    </div>
</template>

<script>
export default {
    name: "FacilityOfficeSplitChart",
    props: {
        academic: { type: Number, default: 0 },
        admin: { type: Number, default: 0 },
    },
    emits: ["select"],
    data() {
        return {
            series: [],
            officeKeys: ["AcademicOffices", "AdminOffices"],
            chartOptions: {
                chart: {
                    type: "radialBar",
                    fontFamily: "inherit",
                    toolbar: { show: false },
                    animations: { enabled: true, speed: 450 },
                },
                colors: ["#b45309", "#0f766e"],
                labels: ["Academic", "Admin"],
                plotOptions: {
                    radialBar: {
                        hollow: { size: "28%" },
                        track: {
                            background: "#f5f5f4",
                            strokeWidth: "100%",
                        },
                        dataLabels: {
                            name: {
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#78716c",
                            },
                            value: {
                                fontSize: "18px",
                                fontWeight: 800,
                                color: "#1c1917",
                                formatter: function (val) {
                                    return Math.round(val) + "%";
                                },
                            },
                            total: {
                                show: true,
                                label: "Offices",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#78716c",
                                formatter: function () {
                                    return "0";
                                },
                            },
                        },
                    },
                },
                legend: {
                    show: true,
                    position: "bottom",
                    fontSize: "12px",
                    markers: { radius: 10 },
                },
                stroke: { lineCap: "round" },
                states: {
                    hover: {
                        filter: { type: "lighten", value: 0.08 },
                    },
                    active: {
                        allowMultipleDataPointsSelection: false,
                        filter: { type: "darken", value: 0.12 },
                    },
                },
            },
        };
    },
    computed: {
        hasData() {
            return (this.academic || 0) + (this.admin || 0) > 0;
        },
    },
    watch: {
        academic: {
            handler: "refresh",
            immediate: true,
        },
        admin: {
            handler: "refresh",
        },
    },
    methods: {
        refresh() {
            var academic = this.academic || 0;
            var admin = this.admin || 0;
            var total = academic + admin;
            if (!total) {
                this.series = [];
                return;
            }

            var counts = [academic, admin];
            this.series = [
                Math.round((academic / total) * 100),
                Math.round((admin / total) * 100),
            ];
            this.chartOptions = Object.assign({}, this.chartOptions, {
                legend: Object.assign({}, this.chartOptions.legend, {
                    formatter: function (seriesName, opts) {
                        return seriesName + " · " + counts[opts.seriesIndex];
                    },
                }),
                plotOptions: Object.assign({}, this.chartOptions.plotOptions, {
                    radialBar: Object.assign(
                        {},
                        this.chartOptions.plotOptions.radialBar,
                        {
                            dataLabels: Object.assign(
                                {},
                                this.chartOptions.plotOptions.radialBar.dataLabels,
                                {
                                    total: Object.assign(
                                        {},
                                        this.chartOptions.plotOptions.radialBar
                                            .dataLabels.total,
                                        {
                                            formatter: function () {
                                                return String(total);
                                            },
                                        }
                                    ),
                                }
                            ),
                        }
                    ),
                }),
            });
        },
        emitOfficeSelect(index) {
            if (index < 0 || index >= this.officeKeys.length) return;
            var labels = this.chartOptions.labels || [];
            this.$emit("select", {
                key: this.officeKeys[index],
                label: labels[index] || "",
            });
        },
        onSliceSelect(event, chartContext, config) {
            var index = -1;
            if (config && typeof config.dataPointIndex === "number" && config.dataPointIndex >= 0) {
                index = config.dataPointIndex;
            } else if (config && typeof config.seriesIndex === "number" && config.seriesIndex >= 0) {
                index = config.seriesIndex;
            }
            this.emitOfficeSelect(index);
        },
        onLegendClick(chartContext, seriesIndex) {
            this.emitOfficeSelect(seriesIndex);
        },
    },
};
</script>

<style lang="scss" scoped>
.fac-chart {
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 1rem;
    padding: 1.25rem 1.35rem 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    height: 100%;
}

.fac-chart__eyebrow {
    margin: 0;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-chart__title {
    margin: 0.2rem 0 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1c1917;
}

.fac-chart__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    color: #a8a29e;
    font-size: 0.9rem;
}

.fac-chart :deep(.apexcharts-radialbar-area),
.fac-chart :deep(.apexcharts-legend-series) {
    cursor: pointer;
}
</style>

